import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as broadcastsApi from '../api/broadcastsApi';
import { Card, EmptyState, ErrorBanner, Modal, Pagination, Spinner, TableSkeleton } from '../components/ui';
import { IconPlus, IconRefresh, IconTrash } from '../components/icons';
import {
  AudienceCount,
  BroadcastCampaign,
  BroadcastGroup,
  BroadcastGroupDetail,
  BroadcastFieldName,
  BroadcastParam,
  BroadcastSegment,
  CustomRecipient,
  CampaignRecipient,
  CampaignStatus,
  MetaTemplate,
  MetaTemplateComponent,
  RecipientStatus,
} from '../types/broadcast';
import { capitalize, formatDateTime } from '../utils/format';

// Sample values substituted into the live preview so the admin can sanity-check
// the message. Never sent — each real recipient gets their own value.
const SAMPLE_NAME = 'Priya Sharma';
const SAMPLE_PHONE = '+91 98765 43210';
// Fills the {{n}} preview for the bag-item variable — a scent family for a
// formula, a catalogue name for stock. The former is the commoner case.
const SAMPLE_CLUSTER = 'Oud';

// How each template variable can be filled. Dynamic fields are the per-recipient
// values a broadcast actually has (name, phone); "Custom text" is a fixed value
// sent to everyone — use it for anything the segment doesn't carry (a promo code,
// a launch date, an order id, etc.).
type ParamMode = BroadcastFieldName | 'custom';
const PARAM_MODE_OPTIONS: { value: ParamMode; label: string }[] = [
  { value: 'name', label: "Recipient's name" },
  { value: 'phone', label: "Recipient's phone" },
  { value: 'cluster', label: 'What they left in their bag' },
  { value: 'custom', label: 'Custom text' },
];

// Only these audiences carry a per-recipient bag item. Offering it elsewhere
// would map a variable to an empty string for every recipient — a quietly broken
// message rather than an obviously broken one, so the option is hidden instead.
const CLUSTER_SEGMENTS: BroadcastSegment[] = ['abandoned_cart', 'group', 'custom'];

const SEGMENTS: { value: BroadcastSegment; label: string }[] = [
  { value: 'waitlist_all', label: 'Waitlist — All (WhatsApp consent only)' },
  { value: 'waitlist_unconverted', label: 'Waitlist — Not yet signed up / converted' },
  { value: 'registered_users', label: 'All registered users with phone number' },
  { value: 'abandoned_cart', label: 'Abandoned cart — items left in the bag, never bought' },
  { value: 'custom', label: 'Specific contact (enter manually)' },
];

// 'group' is not listed above: its options are the saved groups themselves,
// rendered from live data in their own <optgroup>.

// Segments whose size is fetched live. 'custom' has no DB-resolved audience.
const COUNTED_SEGMENTS = SEGMENTS.filter((s) => s.value !== 'custom');

const SEGMENT_LABEL: Record<string, string> = SEGMENTS.reduce(
  (acc, s) => ({ ...acc, [s.value]: s.label }),
  {} as Record<string, string>
);

// ── Template parsing helpers ───────────────────────────────────────────────────

function findComponent(t: MetaTemplate | undefined, type: string): MetaTemplateComponent | undefined {
  return t?.components?.find((c) => c.type?.toUpperCase() === type);
}

function bodyText(t: MetaTemplate | undefined): string {
  return findComponent(t, 'BODY')?.text ?? '';
}

// Unique {{N}} indices in the body text, ascending.
function bodyVarIndices(text: string): number[] {
  const set = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) set.add(Number(m[1]));
  return Array.from(set).sort((a, b) => a - b);
}

// Substitute {{n}} placeholders with their preview values, leaving any index
// without a value as the literal placeholder.
//
// Some approved bodies print the currency symbol themselves ("added ₹{{2}} in
// store credits"), so a value that also carries it would preview as "₹₹200". When
// the character immediately before the placeholder is already a ₹, the value's
// leading ₹ is dropped. Matched on the placeholder's own offset rather than a
// lookup, so a body that repeats an index resolves each occurrence in place.
function substituteParams(text: string, params: Record<number, string>): string {
  return text.replace(/\{\{(\d+)\}\}/g, (match, index, offset: number) => {
    const value = params[Number(index)] ?? match;
    const charBefore = offset > 0 ? text[offset - 1] : '';
    if (charBefore === '₹' && value.charAt(0) === '₹') return value.slice(1);
    return value;
  });
}

// ── Badges ─────────────────────────────────────────────────────────────────────

const CAMPAIGN_BADGE: Record<CampaignStatus, string> = {
  queued:
    'border-edge bg-edge/40 text-sub',
  sending:
    'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-300',
  completed:
    'border-green-500/30 bg-green-500/10 text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300',
  partially_failed:
    'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300',
};

const CAMPAIGN_LABEL: Record<CampaignStatus, string> = {
  queued: 'Queued',
  sending: 'Sending',
  completed: 'Completed',
  partially_failed: 'Partially Failed',
};

function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const cls = CAMPAIGN_BADGE[status] ?? CAMPAIGN_BADGE.queued;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {CAMPAIGN_LABEL[status] ?? status}
    </span>
  );
}

const RECIPIENT_BADGE: Record<RecipientStatus, string> = {
  queued: 'border-edge bg-edge/40 text-sub',
  sent: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-300',
  delivered: 'border-green-500/30 bg-green-500/10 text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300',
  read: 'border-green-500/40 bg-green-500/20 text-green-700 dark:border-green-400/40 dark:bg-green-400/20 dark:text-green-200',
  failed_transient: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300',
  failed_permanent: 'border-red-500/30 bg-red-500/10 text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300',
};

// Plain-English explanations for common Meta delivery error codes, so a policy
// drop (e.g. the marketing frequency cap) doesn't read like a system bug.
const META_ERROR_HELP: Record<string, string> = {
  '131049': "Meta's marketing frequency cap — not delivered to maintain ecosystem engagement. Affects Marketing templates only; use a Utility template or target engaged / opted-in recipients.",
  '131047': 'Re-engagement required — the recipient is outside the messaging window and Meta blocked the template.',
  '131026': 'Undeliverable — the number may not be on WhatsApp or cannot receive this message.',
  '131000': 'Generic Meta failure — usually transient; retry later.',
  '132000': 'Template parameter mismatch — the number of variables sent did not match the approved template.',
  '132001': 'Template does not exist or is not approved in this language.',
  '133010': 'The recipient number is not a valid WhatsApp user.',
};

function recipientError(errorCode: string | null, errorMessage: string | null): { code: string | null; text: string } | null {
  if (!errorCode && !errorMessage) return null;
  const help = errorCode ? META_ERROR_HELP[errorCode] : undefined;
  return { code: errorCode, text: help || errorMessage || 'Failed' };
}

function RecipientStatusBadge({ status }: { status: RecipientStatus }) {
  const cls = RECIPIENT_BADGE[status] ?? RECIPIENT_BADGE.queued;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

const RECIPIENT_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
];

const isFailed = (s: RecipientStatus) => s === 'failed_transient' || s === 'failed_permanent';

// ── Expandable campaign row ─────────────────────────────────────────────────────

function CampaignRow({ campaign, onChanged }: { campaign: BroadcastCampaign; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(
    async (status: string) => {
      setLoading(true);
      setError('');
      try {
        const res = await broadcastsApi.getCampaignRecipients(campaign.id, 1, 200, status);
        setRecipients(res.items);
        setSelected(new Set());
        setLoaded(true);
      } catch (err) {
        setError(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [campaign.id]
  );

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load(statusFilter);
  };

  const changeFilter = (s: string) => {
    setStatusFilter(s);
    load(s);
  };

  const failedIds = recipients.filter((r) => isFailed(r.status)).map((r) => r.id);
  const allFailedSelected = failedIds.length > 0 && failedIds.every((id) => selected.has(id));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAllFailed = () =>
    setSelected((prev) => (allFailedSelected ? new Set() : new Set(failedIds)));

  const doRetry = async (ids?: string[]) => {
    setRetrying(true);
    setError('');
    try {
      await broadcastsApi.retryCampaign(campaign.id, ids);
      await load(statusFilter);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setRetrying(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <>
      <tr className="cursor-pointer transition-colors hover:bg-edge/20" onClick={toggle}>
        <td className="td font-medium">{campaign.campaign_name}</td>
        <td className="td text-sub">{campaign.template_name}</td>
        <td className="td text-sub">{SEGMENT_LABEL[campaign.segment] ?? campaign.segment}</td>
        <td className="td">{campaign.total_recipients}</td>
        <td className="td text-sub">{campaign.sent_count}</td>
        <td className="td text-sub">{campaign.delivered_count}</td>
        <td className={`td ${campaign.failed_count > 0 ? 'text-bad' : 'text-sub'}`}>{campaign.failed_count}</td>
        <td className="td text-sub">{formatDateTime(campaign.created_at)}</td>
        <td className="td">
          <CampaignStatusBadge status={campaign.status} />
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={9} className="bg-edge/10 px-5 py-4">
            {/* Toolbar: filter + refresh + retry */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <select
                className="input max-w-[160px] !py-1.5 !text-xs"
                value={statusFilter}
                onChange={(e) => changeFilter(e.target.value)}
                disabled={loading || retrying}
              >
                {RECIPIENT_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <button
                className="btn-outline !px-3 !py-1.5 !text-xs"
                onClick={() => load(statusFilter)}
                disabled={loading || retrying}
              >
                {loading && <Spinner className="h-3 w-3" />}
                Refresh
              </button>
              <div className="flex-1" />
              <button
                className="btn-outline !px-3 !py-1.5 !text-xs"
                onClick={() => doRetry(Array.from(selected))}
                disabled={retrying || loading || selectedCount === 0}
              >
                {retrying && <Spinner className="h-3 w-3" />}
                Retry selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </button>
              <button
                className="btn-gold !px-3 !py-1.5 !text-xs"
                onClick={() => doRetry(undefined)}
                disabled={retrying || loading || campaign.failed_count === 0}
              >
                {retrying && <Spinner className="h-3 w-3" />}
                Retry all failed
              </button>
            </div>

            {error && <ErrorBanner message={error} />}

            {loading ? (
              <Spinner />
            ) : recipients.length === 0 ? (
              <EmptyState message="No recipients" hint={statusFilter !== 'all' ? 'Try a different filter' : undefined} />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-edge">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className="th w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-edge accent-gold"
                          checked={allFailedSelected}
                          disabled={failedIds.length === 0}
                          onChange={toggleAllFailed}
                          aria-label="Select all failed"
                        />
                      </th>
                      <th className="th">Name</th>
                      <th className="th">Phone</th>
                      <th className="th">Status</th>
                      <th className="th">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-edge">
                    {recipients.map((r) => {
                      const failed = isFailed(r.status);
                      return (
                        <tr key={r.id}>
                          <td className="td">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-edge accent-gold disabled:opacity-30"
                              checked={selected.has(r.id)}
                              disabled={!failed}
                              onChange={() => toggleOne(r.id)}
                              aria-label={`Select ${r.recipient_phone}`}
                            />
                          </td>
                          <td className="td font-medium">{r.recipient_name || '—'}</td>
                          <td className="td text-sub">{r.recipient_phone}</td>
                          <td className="td">
                            <RecipientStatusBadge status={r.status} />
                          </td>
                          <td className="td text-sub">
                            {(() => {
                              const err = recipientError(r.error_code, r.error_message);
                              if (!err) return '—';
                              return (
                                <span className="text-bad">
                                  {err.code ? `[${err.code}] ` : ''}
                                  {err.text}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

// ── Saved groups ──────────────────────────────────────────────────────────────

/**
 * Name a list of contacts and keep it. The contacts come from wherever the
 * admin already assembled them — typed into the composer, or ticked on the
 * cart-abandonment list — so this only asks for the name.
 */
function SaveGroupModal({
  members,
  onClose,
  onSaved,
}: {
  members: CustomRecipient[];
  onClose: () => void;
  onSaved: (name: string, count: number) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await broadcastsApi.createGroup({
        name: name.trim(),
        description: description.trim() || null,
        members,
      });
      onSaved(name.trim(), res.memberCount);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Save as group"
      onClose={() => !saving && onClose()}
      footer={
        <>
          <button className="btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-gold" onClick={save} disabled={saving || name.trim() === ''}>
            {saving && <Spinner />}
            Save group
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} />}
        <div>
          <label className="label">Group name</label>
          <input
            className="input"
            placeholder="e.g. Founders"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <label className="label">Description (optional)</label>
          <input
            className="input"
            placeholder="e.g. Internal numbers for smoke-testing templates"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <p className="text-xs text-sub">
          {members.length} contact{members.length === 1 ? '' : 's'} will be saved. Numbers are
          normalised when stored, and the same number cannot appear twice in one group.
        </p>
      </div>
    </Modal>
  );
}

/** List, rename, inspect and delete saved groups. */
function ManageGroupsModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [groups, setGroups] = useState<BroadcastGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BroadcastGroupDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<BroadcastGroup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setGroups(await broadcastsApi.getGroups());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Members are fetched only when a group is expanded — the list itself needs
  // only counts, and a workspace can hold plenty of groups.
  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    broadcastsApi
      .getGroup(openId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [openId]);

  const rename = async (g: BroadcastGroup) => {
    const next = window.prompt('Rename group', g.name);
    if (!next || next.trim() === g.name) return;
    setBusy(true);
    try {
      await broadcastsApi.renameGroup(g.id, next.trim(), g.description);
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (g: BroadcastGroup) => {
    setBusy(true);
    try {
      await broadcastsApi.deleteGroup(g.id);
      setConfirmDelete(null);
      if (openId === g.id) setOpenId(null);
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (memberId: string) => {
    if (!detail) return;
    setBusy(true);
    try {
      await broadcastsApi.removeGroupMember(detail.id, memberId);
      setDetail(await broadcastsApi.getGroup(detail.id));
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal open title="Saved groups" onClose={onClose} size="lg">
        <div className="space-y-4 p-5">
          {error && <ErrorBanner message={error} onRetry={load} />}
          {loading ? (
            <TableSkeleton rows={3} />
          ) : groups.length === 0 ? (
            <EmptyState
              message="No saved groups yet"
              hint="Pick Specific contact, enter some numbers, then Save as group — or tick people on the cart-abandonment list"
            />
          ) : (
            <div className="divide-y divide-edge rounded-xl border border-edge">
              {groups.map((g) => (
                <div key={g.id}>
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <button
                      type="button"
                      className="flex-1 text-left"
                      onClick={() => setOpenId(openId === g.id ? null : g.id)}
                    >
                      <span className="font-medium text-ink">{g.name}</span>
                      <span className="ml-2 text-xs text-sub">
                        {g.member_count} {g.member_count === 1 ? 'contact' : 'contacts'}
                      </span>
                      {g.description && <p className="mt-0.5 text-xs text-sub">{g.description}</p>}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost !px-2.5 !py-1 !text-xs"
                      onClick={() => rename(g)}
                      disabled={busy}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="btn-ghost !px-2.5 !py-1 !text-xs text-bad"
                      onClick={() => setConfirmDelete(g)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>

                  {openId === g.id && (
                    <div className="border-t border-edge bg-edge/10 px-4 py-3">
                      {!detail ? (
                        <Spinner />
                      ) : detail.members.length === 0 ? (
                        <p className="text-xs text-sub">No contacts in this group.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {detail.members.map((m) => (
                            <li key={m.id} className="flex items-center gap-3 text-sm">
                              <span className="flex-1 text-ink">{m.name?.trim() || 'Unnamed'}</span>
                              <span className="font-mono text-xs text-sub">{m.phone}</span>
                              {m.cluster && <span className="text-xs text-sub">{m.cluster}</span>}
                              <button
                                type="button"
                                className="btn-ghost !px-2 !py-0.5 !text-xs text-bad"
                                onClick={() => removeMember(m.id)}
                                disabled={busy}
                                aria-label={`Remove ${m.phone}`}
                              >
                                <IconTrash className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-sub">
            Deleting a group does not affect campaigns already sent to it — those keep their own
            recipient rows, so history and retries are unchanged.
          </p>
        </div>
      </Modal>

      {confirmDelete && (
        <Modal
          open
          title="Delete group"
          onClose={() => !busy && setConfirmDelete(null)}
          footer={
            <>
              <button className="btn-outline" onClick={() => setConfirmDelete(null)} disabled={busy}>
                Cancel
              </button>
              <button className="btn-gold" onClick={() => doDelete(confirmDelete)} disabled={busy}>
                {busy && <Spinner />}
                Delete
              </button>
            </>
          }
        >
          <p className="text-sm text-sub">
            Delete <span className="font-semibold text-ink">{confirmDelete.name}</span> and its{' '}
            {confirmDelete.member_count} contact
            {confirmDelete.member_count === 1 ? '' : 's'}? Campaigns already sent to it are
            unaffected.
          </p>
        </Modal>
      )}
    </>
  );
}

export default function BroadcastsPage() {
  // History
  const [campaigns, setCampaigns] = useState<BroadcastCampaign[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');

  // Templates + audience counts
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState('');
  const [counts, setCounts] = useState<Partial<Record<BroadcastSegment, AudienceCount>>>({});
  const [groups, setGroups] = useState<BroadcastGroup[]>([]);
  const [groupId, setGroupId] = useState('');
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [saveGroupOpen, setSaveGroupOpen] = useState(false);

  // Form
  const [campaignName, setCampaignName] = useState('');
  const [segment, setSegment] = useState<BroadcastSegment | ''>('');
  const [templateName, setTemplateName] = useState('');
  const [paramValues, setParamValues] = useState<Record<number, { mode: ParamMode; value: string }>>({});
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [buttonUrlParam, setButtonUrlParam] = useState('');
  const [customRecipients, setCustomRecipients] = useState<CustomRecipient[]>([
    { name: '', phone: '', cluster: null },
  ]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const loadCampaigns = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await broadcastsApi.getCampaigns(page, 20);
      setCampaigns(res.items);
      setTotalPages(res.totalPages);
    } catch (err) {
      setHistoryError(apiErrorMessage(err));
    } finally {
      setHistoryLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await broadcastsApi.getGroups());
    } catch {
      // A groups outage must not take the composer down with it — the other
      // audiences still work, and the dropdown simply shows none.
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      setTemplates(await broadcastsApi.getTemplates());
    } catch (err) {
      setTemplatesError(apiErrorMessage(err));
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Fetch live audience counts for every segment (so the dropdown shows accurate
  // numbers). Called on mount and whenever the audience dropdown is focused.
  const loadCounts = useCallback(async () => {
    const entries = await Promise.all(
      COUNTED_SEGMENTS.map(async (s) => {
        try {
          return [s.value, await broadcastsApi.getAudienceCount(s.value)] as const;
        } catch {
          return [s.value, undefined] as const;
        }
      })
    );
    setCounts((prev) => {
      const next = { ...prev };
      for (const [seg, count] of entries) if (count) next[seg] = count;
      return next;
    });
  }, []);

  useEffect(() => {
    loadTemplates();
    loadCounts();
    loadGroups();
  }, [loadTemplates, loadCounts, loadGroups]);

  // Contacts handed over by the cart-abandonment list's "Message selected".
  // They arrive as a 'custom' send so it still goes through the campaign tables
  // and appears in history with a retry, and they carry their own cluster so a
  // {{scent family}} variable resolves per person exactly as a full
  // abandoned_cart send would.
  //
  // The router state is cleared immediately: without that, a refresh or a Back
  // into this page would re-seed the form over whatever the admin had typed.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const seed = (location.state as { seedRecipients?: CustomRecipient[] } | null)?.seedRecipients;
    if (!seed || seed.length === 0) return;
    setSegment('custom');
    setCustomRecipients(seed.map((r) => ({ name: r.name ?? '', phone: r.phone ?? '', cluster: r.cluster ?? null })));
    navigate('.', { replace: true, state: null });
  }, [location.state, navigate]);

  // A group deleted from the manage modal must not stay selected as the target:
  // the send would fail at create time with a 404 the admin never asked for.
  // Waits for the list to be non-empty so a still-loading fetch cannot clear a
  // perfectly valid selection.
  useEffect(() => {
    if (!groupId || groups.length === 0) return;
    if (!groups.some((g) => g.id === groupId)) {
      setGroupId('');
      setSegment('');
    }
  }, [groups, groupId]);

  // A variable mapped to the bag item only resolves on an audience that
  // carries one. Switching to an audience that doesn't would otherwise leave a
  // mapping the server rejects, so it falls back to the name — the same default
  // a freshly picked template gets.
  useEffect(() => {
    if (segment !== '' && CLUSTER_SEGMENTS.includes(segment)) return;
    setParamValues((prev) => {
      if (!Object.values(prev).some((pv) => pv.mode === 'cluster')) return prev;
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        const i = Number(k);
        if (next[i].mode === 'cluster') next[i] = { mode: 'name', value: '' };
      }
      return next;
    });
  }, [segment]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === templateName),
    [templates, templateName]
  );
  const body = bodyText(selectedTemplate);
  const varIndices = useMemo(() => bodyVarIndices(body), [body]);
  const headerComp = findComponent(selectedTemplate, 'HEADER');
  const footerComp = findComponent(selectedTemplate, 'FOOTER');
  const buttonsComp = findComponent(selectedTemplate, 'BUTTONS');
  const headerFormat = (headerComp?.format ?? '').toUpperCase();
  const needsHeaderImage = headerFormat === 'IMAGE';
  const isCustom = segment === 'custom';
  const isGroup = segment === 'group';
  const selectedGroup = groups.find((g) => g.id === groupId);

  // A dynamic URL button is one whose URL ends in a {{n}} variable — Meta then
  // requires the send to supply that suffix.
  const dynamicUrlButton = buttonsComp?.buttons?.find(
    (b) => (b.type ?? '').toUpperCase() === 'URL' && (b.url ?? '').includes('{{')
  );
  const needsButtonUrl = Boolean(dynamicUrlButton);

  // When the template changes, reset the parameter mapping — default each variable
  // to the recipient's name (the common "Hi {{1}}" case; admin can switch later).
  const onSelectTemplate = (name: string) => {
    setTemplateName(name);
    const tpl = templates.find((t) => t.name === name);
    const idxs = bodyVarIndices(bodyText(tpl));
    const next: Record<number, { mode: ParamMode; value: string }> = {};
    for (const i of idxs) next[i] = { mode: 'name', value: '' };
    setParamValues(next);
    setHeaderImageUrl('');
    setButtonUrlParam('');
    setSuccessMsg('');
  };

  const setParam = (index: number, patch: Partial<{ mode: ParamMode; value: string }>) => {
    setParamValues((prev) => ({ ...prev, [index]: { ...prev[index], ...patch } }));
  };

  // Resolve a variable's preview value using sample data for dynamic fields.
  const resolveVar = (index: number): string => {
    const pv = paramValues[index];
    if (!pv || pv.mode === 'name') return SAMPLE_NAME;
    if (pv.mode === 'phone') return SAMPLE_PHONE;
    if (pv.mode === 'cluster') return SAMPLE_CLUSTER;
    return pv.value.trim() === '' ? `{{${index}}}` : pv.value;
  };

  const previewValues: Record<number, string> = {};
  for (const i of varIndices) previewValues[i] = resolveVar(i);
  const previewBody = substituteParams(body, previewValues);

  const paramsFilled = varIndices.every((i) => {
    const pv = paramValues[i];
    if (!pv) return false;
    return pv.mode !== 'custom' || pv.value.trim() !== '';
  });

  const setCustomRecipient = (i: number, patch: Partial<CustomRecipient>) =>
    setCustomRecipients((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addCustomRow = () => setCustomRecipients((prev) => [...prev, { name: '', phone: '', cluster: null }]);
  const removeCustomRow = (i: number) =>
    setCustomRecipients((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // Rows with both a name and a phone are the ones actually sent.
  const validCustomRecipients = customRecipients
    .map((r) => ({ name: r.name.trim(), phone: r.phone.trim(), cluster: r.cluster ?? null }))
    .filter((r) => r.name && r.phone);

  const customValid = !isCustom || validCustomRecipients.length > 0;
  const groupValid = !isGroup || Boolean(selectedGroup && selectedGroup.member_count > 0);
  const headerImageValid = !needsHeaderImage || headerImageUrl.trim() !== '';
  const buttonUrlValid = !needsButtonUrl || buttonUrlParam.trim() !== '';

  const canSend =
    campaignName.trim() !== '' &&
    segment !== '' &&
    templateName !== '' &&
    paramsFilled &&
    customValid &&
    groupValid &&
    headerImageValid &&
    buttonUrlValid &&
    !sending;

  const buildParameters = (): BroadcastParam[] =>
    varIndices.map((i) => {
      const pv = paramValues[i];
      return pv.mode === 'custom'
        ? { index: i, type: 'static', value: pv.value }
        : { index: i, type: 'dynamic', field: pv.mode };
    });

  // Custom sends target the contacts entered; otherwise the live count. Always
  // `.count` — who is REACHABLE. `.total` counts abandoned users including those
  // with no phone, and quoting it as a send size would promise messages that
  // cannot be sent.
  const audience = segment && !isCustom && !isGroup ? counts[segment] : undefined;
  // A group's size is its stored member count — every member was normalized to a
  // deliverable number when it was saved, so there is nothing further to resolve.
  const targetCount = isCustom
    ? validCustomRecipients.length
    : isGroup
      ? selectedGroup?.member_count
      : audience?.count;
  const withoutPhone = audience?.without_phone ?? 0;

  const handleConfirmSend = async () => {
    if (segment === '') return;
    setSending(true);
    setFormError('');
    try {
      const res = await broadcastsApi.createBroadcast({
        campaignName: campaignName.trim(),
        segment,
        templateName,
        parameters: buildParameters(),
        headerImageUrl: needsHeaderImage ? headerImageUrl.trim() : undefined,
        buttonUrlParam: needsButtonUrl ? buttonUrlParam.trim() : undefined,
        recipients: isCustom ? validCustomRecipients : undefined,
        groupId: isGroup ? groupId : undefined,
      });
      setConfirmOpen(false);
      setSuccessMsg(`Campaign queued — sending to ${res.recipientCount} recipient${res.recipientCount === 1 ? '' : 's'}.`);
      // Reset the form
      setCampaignName('');
      setSegment('');
      setGroupId('');
      setTemplateName('');
      setParamValues({});
      setHeaderImageUrl('');
      setButtonUrlParam('');
      setCustomRecipients([{ name: '', phone: '' }]);
      setPage(1);
      await loadCampaigns();
    } catch (err) {
      setFormError(apiErrorMessage(err));
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Broadcasts</h1>
        <p className="text-sm text-sub">Send an approved WhatsApp template to a segment of your audience</p>
      </div>

      {/* ── Campaign history ─────────────────────────────────────────────── */}
      <Card
        title="Campaign history"
        bodyClassName="!p-0"
        action={
          <button
            className="btn-outline !px-3 !py-1.5 !text-xs"
            onClick={loadCampaigns}
            disabled={historyLoading}
          >
            {historyLoading ? <Spinner className="h-3 w-3" /> : <IconRefresh className="h-3.5 w-3.5" />}
            Refresh
          </button>
        }
      >
        {historyError ? (
          <div className="p-5">
            <ErrorBanner message={historyError} onRetry={loadCampaigns} />
          </div>
        ) : historyLoading ? (
          <TableSkeleton rows={5} />
        ) : campaigns.length === 0 ? (
          <EmptyState message="No campaigns yet" hint="Create your first broadcast below" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Campaign Name</th>
                    <th className="th">Template</th>
                    <th className="th">Audience</th>
                    <th className="th">Recipients</th>
                    <th className="th">Sent</th>
                    <th className="th">Delivered</th>
                    <th className="th">Failed</th>
                    <th className="th">Started At</th>
                    <th className="th">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {campaigns.map((c) => (
                    <CampaignRow key={c.id} campaign={c} onChanged={loadCampaigns} />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {/* ── New broadcast ────────────────────────────────────────────────── */}
      <Card title="New broadcast">
        <div className="space-y-5">
          {successMsg && (
            <div className="rounded-xl border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-600 dark:text-green-300">
              {successMsg}
            </div>
          )}
          {formError && <ErrorBanner message={formError} />}

          {/* 1. Campaign name */}
          <div>
            <label className="label">Campaign name</label>
            <input
              className="input"
              placeholder="e.g. Launch announcement — July 2026"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">Internal label only — never shown to recipients.</p>
          </div>

          {/* 2. Target audience */}
          <div>
            <label className="label">Target audience</label>
            <select
              className="input"
              value={isGroup ? `group:${groupId}` : segment}
              onFocus={loadCounts}
              onChange={(e) => {
                const v = e.target.value;
                // Saved groups share one segment and differ by id, so the option
                // value carries both and is split back apart here.
                if (v.startsWith('group:')) {
                  setSegment('group');
                  setGroupId(v.slice('group:'.length));
                } else {
                  setSegment(v as BroadcastSegment);
                  setGroupId('');
                }
              }}
            >
              <option value="">Select an audience…</option>
              {SEGMENTS.map((s) => {
                if (s.value === 'custom') {
                  return (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  );
                }
                const c = counts[s.value]?.count;
                return (
                  <option key={s.value} value={s.value}>
                    {s.label} {c === undefined ? '(…)' : `— ${c} ${c === 1 ? 'person' : 'people'}`}
                  </option>
                );
              })}
              {groups.length > 0 && (
                <optgroup label="Saved groups">
                  {groups.map((g) => (
                    <option key={g.id} value={`group:${g.id}`}>
                      {g.name} — {g.member_count} {g.member_count === 1 ? 'contact' : 'contacts'}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                className="btn-ghost !px-2.5 !py-1 !text-xs"
                onClick={() => setManageGroupsOpen(true)}
              >
                Manage groups
              </button>
              {isGroup && selectedGroup?.description && (
                <span className="text-xs text-sub">{selectedGroup.description}</span>
              )}
            </div>

            {/* Who this audience loses, and why. Shown only when someone is
                actually being skipped — the reachable figure above is the send
                size, and this says what it does not include. */}
            {withoutPhone > 0 && (
              <p className="mt-1.5 text-xs text-sub">
                {targetCount ?? 0} reachable by WhatsApp. {withoutPhone} have no phone number on file and will be
                skipped.
              </p>
            )}

            {/* Custom contacts — add one or more people manually */}
            {isCustom && (
              <div className="mt-3 space-y-3 rounded-xl border border-edge p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-sub">Contacts</p>
                {customRecipients.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[160px] flex-1">
                      <label className="label">Name</label>
                      <input
                        className="input"
                        placeholder="e.g. Priya Sharma"
                        value={r.name}
                        onChange={(e) => setCustomRecipient(i, { name: e.target.value })}
                      />
                    </div>
                    {r.cluster && (
                      <div className="min-w-[120px]">
                        <label className="label">In their bag</label>
                        <p className="input !bg-edge/30 !text-sub" title="Carried from the cart-abandonment list">
                          {r.cluster}
                        </p>
                      </div>
                    )}
                    <div className="min-w-[160px] flex-1">
                      <label className="label">WhatsApp number</label>
                      <input
                        className="input"
                        placeholder="e.g. 9876543210"
                        value={r.phone}
                        onChange={(e) => setCustomRecipient(i, { phone: e.target.value })}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-ghost !px-2.5 !py-2 text-bad disabled:opacity-30"
                      onClick={() => removeCustomRow(i)}
                      disabled={customRecipients.length <= 1}
                      aria-label="Remove contact"
                    >
                      <IconTrash className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3">
                  <button type="button" className="btn-outline !px-3 !py-1.5 !text-xs" onClick={addCustomRow}>
                    <IconPlus className="h-4 w-4" />
                    Add contact
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-sub">
                      {validCustomRecipients.length} valid contact{validCustomRecipients.length === 1 ? '' : 's'} · +91 assumed if no country code
                    </span>
                    {/* Keep this list for next time — the point of a group. */}
                    <button
                      type="button"
                      className="btn-outline !px-3 !py-1.5 !text-xs"
                      disabled={validCustomRecipients.length === 0}
                      onClick={() => setSaveGroupOpen(true)}
                    >
                      Save as group
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 3. Template */}
          <div>
            <label className="label">Template</label>
            {templatesError ? (
              <ErrorBanner message={templatesError} onRetry={loadTemplates} />
            ) : (
              <select
                className="input"
                value={templateName}
                disabled={templatesLoading}
                onChange={(e) => onSelectTemplate(e.target.value)}
              >
                <option value="">{templatesLoading ? 'Loading approved templates…' : 'Select a template…'}</option>
                {templates.map((t) => (
                  <option key={`${t.name}:${t.language}`} value={t.name}>
                    {t.name} — {capitalize((t.category || '').toLowerCase())}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Image header URL — required for templates with an IMAGE header */}
          {selectedTemplate && needsHeaderImage && (
            <div>
              <label className="label">Header image URL</label>
              <input
                className="input"
                placeholder="https://…/image.jpg"
                value={headerImageUrl}
                onChange={(e) => setHeaderImageUrl(e.target.value)}
              />
              <p className="mt-1.5 text-xs text-sub">
                This template has an image header — a public image URL is required to send it.
              </p>
            </div>
          )}

          {/* Dynamic URL button — the template's button URL ends in {{1}} */}
          {selectedTemplate && needsButtonUrl && (
            <div>
              <label className="label">Button URL value</label>
              <input
                className="input"
                placeholder="e.g. summer-sale  or  order/12345"
                value={buttonUrlParam}
                onChange={(e) => setButtonUrlParam(e.target.value)}
              />
              <p className="mt-1.5 text-xs text-sub">
                This template's “{dynamicUrlButton?.text || 'link'}” button has a dynamic URL
                {dynamicUrlButton?.url ? ` (${dynamicUrlButton.url})` : ''} — enter the value that fills its{' '}
                <span className="font-mono">{'{{1}}'}</span>. Sent as-is to everyone.
              </p>
            </div>
          )}

          {/* Non-image media headers aren't supported by the broadcast builder yet */}
          {selectedTemplate && (headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-300">
              This template has a {headerFormat.toLowerCase()} header, which the broadcast builder doesn't support yet.
              Pick a template with a text or image header.
            </div>
          )}

          {/* 4. Template parameters */}
          {selectedTemplate && varIndices.length > 0 && (
            <div className="space-y-3 rounded-xl border border-edge p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-sub">Template parameters</p>
              {varIndices.map((i) => {
                const pv = paramValues[i] ?? { mode: 'name' as ParamMode, value: '' };
                return (
                  <div key={i} className="flex flex-wrap items-center gap-3">
                    <span className="w-12 shrink-0 font-mono text-sm text-sub">{`{{${i}}}`}</span>
                    <select
                      className="input max-w-[200px]"
                      value={pv.mode}
                      onChange={(e) => setParam(i, { mode: e.target.value as ParamMode })}
                    >
                      {PARAM_MODE_OPTIONS
                        .filter((o) => o.value !== 'cluster' || (segment !== '' && CLUSTER_SEGMENTS.includes(segment)))
                        .map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                    </select>
                    {pv.mode === 'custom' && (
                      <input
                        className="input flex-1"
                        placeholder="Fixed value for all recipients (e.g. promo code, date)"
                        value={pv.value}
                        onChange={(e) => setParam(i, { value: e.target.value })}
                      />
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-sub">
                Dynamic fields are filled per recipient. For anything a broadcast doesn't carry (order id, amount,
                promo code, dates), use <span className="font-medium">Custom text</span> — it's sent as-is to everyone.
              </p>
            </div>
          )}

          {/* 5. Preview */}
          {selectedTemplate && (
            <div>
              <label className="label">Preview</label>
              <div className="max-w-md rounded-2xl border border-edge bg-edge/20 p-4 text-sm">
                {headerComp && (
                  <div className="mb-2">
                    {needsHeaderImage && headerImageUrl.trim() ? (
                      <img
                        src={headerImageUrl.trim()}
                        alt="Header preview"
                        className="max-h-40 w-full rounded-lg object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : headerComp.format === 'IMAGE' || headerComp.format === 'VIDEO' || headerComp.format === 'DOCUMENT' ? (
                      <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-edge text-xs text-sub">
                        [{capitalize((headerComp.format || '').toLowerCase())} header]
                      </div>
                    ) : (
                      headerComp.text && <p className="font-semibold">{headerComp.text}</p>
                    )}
                  </div>
                )}
                <p className="whitespace-pre-wrap text-ink">{previewBody || '—'}</p>
                {footerComp?.text && <p className="mt-2 text-xs text-sub">{footerComp.text}</p>}
                {buttonsComp?.buttons && buttonsComp.buttons.length > 0 && (
                  <div className="mt-3 space-y-1 border-t border-edge pt-2">
                    {buttonsComp.buttons.map((b, idx) => (
                      <p key={idx} className="text-center text-xs font-medium text-gold">
                        {b.text || b.type}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-sub">
                Sample values shown for dynamic fields — each recipient gets their own name.
              </p>
            </div>
          )}

          {/* 6. Send */}
          <div className="flex items-center gap-3 border-t border-edge pt-4">
            <button className="btn-gold" disabled={!canSend} onClick={() => setConfirmOpen(true)}>
              Send broadcast
            </button>
            {segment && (
              <span className="text-xs text-sub">
                {targetCount === undefined ? 'Counting audience…' : `${targetCount} recipient${targetCount === 1 ? '' : 's'} in this segment`}
              </span>
            )}
          </div>
        </div>
      </Card>

      {saveGroupOpen && (
        <SaveGroupModal
          members={validCustomRecipients}
          onClose={() => setSaveGroupOpen(false)}
          onSaved={async (name, count) => {
            setSaveGroupOpen(false);
            setSuccessMsg(`Saved "${name}" — ${count} contact${count === 1 ? '' : 's'}.`);
            await loadGroups();
          }}
        />
      )}

      {manageGroupsOpen && (
        <ManageGroupsModal
          onClose={() => setManageGroupsOpen(false)}
          onChanged={loadGroups}
        />
      )}

      {/* Confirmation modal */}
      <Modal
        open={confirmOpen}
        title="Confirm broadcast"
        onClose={() => !sending && setConfirmOpen(false)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleConfirmSend} disabled={sending}>
              {sending && <Spinner />}
              Send now
            </button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-sub">
          <p>
            You are about to send <span className="font-semibold text-ink">{templateName}</span> to{' '}
            <span className="font-semibold text-ink">
              {targetCount === undefined ? 'all' : targetCount} recipient{targetCount === 1 ? '' : 's'}
            </span>{' '}
            in <span className="font-semibold text-ink">{segment ? SEGMENT_LABEL[segment] : ''}</span>.
          </p>
          <p className="text-bad">This cannot be undone. Confirm?</p>
        </div>
      </Modal>
    </div>
  );
}
